import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Target, Gamepad2, DollarSign, Users, Loader2, 
  CheckCircle, ArrowRight, AlertCircle, Wallet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuickMatch = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [entryAmount, setEntryAmount] = useState('10');
  const [config, setConfig] = useState(null);
  const [searching, setSearching] = useState(false);
  const [matchResult, setMatchResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [gamesRes, configRes] = await Promise.all([
        axios.get(`${API}/matches/games`),
        axios.get(`${API}/matches/config`)
      ]);
      setGames(gamesRes.data);
      if (gamesRes.data.length > 0) {
        setSelectedGame(gamesRes.data[0]);
      }
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleQuickMatch = async () => {
    if (!selectedGame) {
      toast.error('Please select a game');
      return;
    }
    
    const amount = parseFloat(entryAmount);
    const rate = config?.credit_conversion_rate || 100;
    const entryCredits = amount * rate;
    
    if (entryCredits > (user?.credits_balance || 0)) {
      toast.error('Insufficient credits');
      return;
    }

    setSearching(true);
    setMatchResult(null);

    try {
      const response = await axios.post(
        `${API}/matches/quick-match`,
        {
          game_id: selectedGame.id,
          wager_amount: amount
        },
        { withCredentials: true }
      );

      setMatchResult(response.data);
      await refreshUser();

      if (response.data.status === 'matched') {
        toast.success(`Matched with ${response.data.opponent_name}!`);
        setTimeout(() => {
          navigate(`/match/${response.data.match_id}`);
        }, 2000);
      } else {
        toast.success('Added to queue! Waiting for opponent...');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to find match');
    } finally {
      setSearching(false);
    }
  };

  const quickAmounts = [5, 10, 25, 50, 100];

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="quick-match-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/30 mb-4">
            <Target className="w-4 h-4 text-[#CCFF00]" />
            <span className="text-sm text-[#CCFF00] font-medium">Instant Matchmaking</span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-2">Quick Match</h1>
          <p className="text-white/50">Find an opponent instantly based on similar entry fees</p>
        </div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8"
        >
          {/* Game Selection */}
          <div className="mb-8">
            <label className="text-sm text-white/50 mb-3 block">Select Game</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              {games.map(game => (
                <motion.div
                  key={game.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedGame(game)}
                  className={`p-3 sm:p-4 rounded-xl cursor-pointer transition-all text-center ${
                    selectedGame?.id === game.id
                      ? 'bg-[#7000FF]/20 border-2 border-[#7000FF]'
                      : 'bg-white/5 border-2 border-transparent hover:border-white/20'
                  }`}
                  data-testid={`quick-game-${game.id}`}
                >
                  <Gamepad2 className={`w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 ${
                    selectedGame?.id === game.id ? 'text-[#7000FF]' : 'text-white/50'
                  }`} />
                  <p className="text-[10px] sm:text-xs font-medium leading-tight">{game.name}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Entry Fee */}
          <div className="mb-8">
            <label className="text-sm text-white/50 mb-3 block">Entry Fee Amount</label>
            <div className="relative mb-4">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-mono text-xl">$</span>
              <Input
                type="number"
                placeholder="0.00"
                value={entryAmount}
                onChange={(e) => setEntryAmount(e.target.value)}
                className="pl-10 text-3xl font-mono input-dark h-16 text-center"
                min="1"
                max="500"
                data-testid="quick-entry-input"
              />
            </div>

            {entryAmount && parseFloat(entryAmount) > 0 && (
              <div className="p-3 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/20 flex items-center justify-between mb-4">
                <span className="text-sm text-white/70">Competition Access:</span>
                <span className="font-mono font-bold text-[#00F0FF]">
                  {(parseFloat(entryAmount) * (config?.credit_conversion_rate || 100)).toLocaleString()} credits
                </span>
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-2">
              {quickAmounts.map(amount => (
                <Button
                  key={amount}
                  variant="outline"
                  size="sm"
                  onClick={() => setEntryAmount(amount.toString())}
                  disabled={(user?.credits_balance || 0) < amount * (config?.credit_conversion_rate || 100)}
                  className={`btn-secondary ${entryAmount === amount.toString() ? 'border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]' : ''}`}
                >
                  ${amount} → {(amount * (config?.credit_conversion_rate || 100)).toLocaleString()}
                </Button>
              ))}
            </div>
          </div>

          {/* Credits Info */}
          <div className="p-4 rounded-xl bg-white/5 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-white/50">Your Credits</span>
              <span className="font-mono text-lg text-[#00F0FF]">{(user?.credits_balance || 0).toLocaleString()}</span>
            </div>
          </div>

          {parseFloat(entryAmount) * (config?.credit_conversion_rate || 100) > (user?.credits_balance || 0) && (
            <div className="p-4 rounded-lg bg-[#FF2E2E]/10 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-[#FF2E2E]" />
                <span className="text-sm text-[#FF2E2E]">Insufficient credits</span>
              </div>
              <Button
                onClick={() => navigate('/wallet')}
                className="w-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
                data-testid="quick-purchase-btn"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Add Credits
              </Button>
            </div>
          )}

          {/* Match Result */}
          {matchResult && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`p-6 rounded-xl mb-6 text-center ${
                matchResult.status === 'matched' 
                  ? 'bg-[#00FF94]/10 border border-[#00FF94]/30'
                  : 'bg-[#FFB800]/10 border border-[#FFB800]/30'
              }`}
            >
              {matchResult.status === 'matched' ? (
                <>
                  <CheckCircle className="w-12 h-12 text-[#00FF94] mx-auto mb-3" />
                  <p className="font-heading font-bold text-xl mb-1">Match Found!</p>
                  <p className="text-white/70">
                    Playing against <span className="text-[#00F0FF] font-bold">{matchResult.opponent_name}</span>
                  </p>
                  <p className="text-white/50 text-sm mt-2">Redirecting to match...</p>
                </>
              ) : (
                <>
                  <Users className="w-12 h-12 text-[#FFB800] mx-auto mb-3" />
                  <p className="font-heading font-bold text-xl mb-1">Added to Queue</p>
                  <p className="text-white/70">{matchResult.message}</p>
                  <Button
                    onClick={() => navigate(`/match/${matchResult.match_id}`)}
                    className="mt-4 btn-secondary"
                  >
                    View Match <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </>
              )}
            </motion.div>
          )}

          {/* Find Match Button */}
          <Button
            onClick={handleQuickMatch}
            disabled={searching || !selectedGame || !entryAmount || parseFloat(entryAmount) * (config?.credit_conversion_rate || 100) > (user?.credits_balance || 0)}
            className="w-full btn-primary clip-slant py-6 text-lg"
            data-testid="find-match-btn"
          >
            {searching ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Finding Match...
              </>
            ) : (
              <>
                <Target className="w-5 h-5 mr-2" />
                Find Match Instantly
              </>
            )}
          </Button>

          {/* Info */}
          <div className="mt-6 p-4 rounded-lg bg-white/5">
            <p className="text-xs text-white/40 text-center">
              Quick Match will find an opponent with a similar entry fee (±20%). 
              If no match is found, you'll be added to the queue.
            </p>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default QuickMatch;
